import { useState, useEffect, useRef } from "react";
import { OrdersApi, type OrderPreviewRequest, type OrderPreviewResponse } from "@nordex/core";
import { toDecimalString } from "@nordex/core/types";

interface UseOrderPreviewOptions {
  userId: string | null;
  market: string;
  side: "long" | "short";
  size: number;
  price?: number;
  leverage: number;
  orderType: "market" | "limit";
  enabled?: boolean;
}

interface UseOrderPreviewResult {
  preview: OrderPreviewResponse | null;
  isLoading: boolean;
  error: string | null;
}

// Throttle interval: 5 seconds (liquidation price is guidance only)
// This reduces API calls significantly while still providing useful estimates
const THROTTLE_INTERVAL = 5000;

/**
 * Hook to fetch order preview from backend
 * Throttled to prevent excessive API calls (5 second minimum between calls)
 * 
 * Industry standard: Backend calculates all risk metrics authoritatively
 * This eliminates the $500+ discrepancy caused by frontend calculations
 * 
 * Features:
 * - Keeps previous value while loading (no flicker)
 * - Throttles to 5 seconds between API calls
 * - For market orders, doesn't include price (backend uses mark price)
 * 
 * @example
 * const { preview, isLoading, error } = useOrderPreview({
 *   userId: "user123",
 *   market: "BTC-USD",
 *   side: "long",
 *   size: 1,
 *   leverage: 10,
 *   orderType: "market",
 *   enabled: true,
 * });
 * 
 * // Display backend-calculated liquidation price
 * const liqPrice = preview?.estimated_liquidation_price;
 */
export function useOrderPreview(options: UseOrderPreviewOptions): UseOrderPreviewResult {
  const [preview, setPreview] = useState<OrderPreviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Throttle tracking
  const lastFetchTime = useRef<number>(0);
  const pendingFetch = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Track previous inputs to detect meaningful changes
  const prevInputsRef = useRef<string>("");

  useEffect(() => {
    // Don't fetch if disabled or invalid inputs
    if (!options.enabled || !options.userId || options.size <= 0) {
      setPreview(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // For market orders, don't include price - backend uses its own mark price
    // This prevents unnecessary recalculations on every price tick
    // Backend code: "market" => mark_price (ignores provided price anyway)
    const effectivePrice = options.orderType === "limit" ? options.price : undefined;
    
    // Create a key for input comparison (only meaningful inputs)
    // For market orders, price is excluded since backend ignores it
    const inputKey = `${options.market}-${options.side}-${options.size.toFixed(8)}-${effectivePrice ?? "market"}-${options.leverage}-${options.orderType}`;
    
    // Skip if inputs haven't changed meaningfully
    if (inputKey === prevInputsRef.current && preview !== null) {
      return;
    }
    prevInputsRef.current = inputKey;

    const fetchPreview = async () => {
      // Don't show loading if we have a previous value (prevents flicker)
      if (!preview) {
        setIsLoading(true);
      }
      setError(null);

      try {
        const request: OrderPreviewRequest = {
          market: options.market,
          side: options.side,
          size: toDecimalString(options.size),
          price: effectivePrice ? toDecimalString(effectivePrice) : undefined,
          leverage: toDecimalString(options.leverage),
          order_type: options.orderType,
        };

        const response = await OrdersApi.previewOrder(request);
        setPreview(response);
        lastFetchTime.current = Date.now();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch preview";
        setError(errorMessage);
        // Keep previous preview on error (don't clear it) - prevents flicker on transient errors
        console.error("Order preview error:", errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    // Clear any pending fetch
    if (pendingFetch.current) {
      clearTimeout(pendingFetch.current);
    }

    // Check if we should throttle
    const timeSinceLastFetch = Date.now() - lastFetchTime.current;
    
    if (timeSinceLastFetch >= THROTTLE_INTERVAL) {
      // Enough time has passed, fetch immediately
      fetchPreview();
    } else {
      // Schedule fetch after throttle interval
      const delay = THROTTLE_INTERVAL - timeSinceLastFetch;
      pendingFetch.current = setTimeout(fetchPreview, delay);
    }

    return () => {
      if (pendingFetch.current) {
        clearTimeout(pendingFetch.current);
      }
    };
  }, [
    options.enabled,
    options.userId,
    options.market,
    options.side,
    options.size,
    options.price,
    options.leverage,
    options.orderType,
    preview,
  ]);

  return { preview, isLoading, error };
}
